// ────────────────────────────────────────────────────────────
// TRACTS — searchable list, the Deal Desk index
// ────────────────────────────────────────────────────────────
// The tract is the top-level object. This page lists every tract
// the title agent has written, searchable by TRS.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TractSummary } from './types';
import { getDealSource } from './data/source';

export function TractsList() {
  const [tracts, setTracts] = useState<TractSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    getDealSource().listTracts()
      .then(t => { if (alive) setTracts(t); })
      .catch(e => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  const visible = useMemo(() => {
    if (!tracts) return [];
    const q = query.trim().toLowerCase();
    if (!q) return tracts;
    return tracts.filter(t =>
      t.trs.toLowerCase().includes(q) || t.county.toLowerCase().includes(q));
  }, [tracts, query]);

  return (
    <div className="dd-page dd-tracts-page">
      <div className="dd-tracts-head">
        <h1 className="dd-owner-name">Tracts</h1>
        <input
          className="dd-request-input dd-tracts-search"
          type="text"
          placeholder="Search by TRS · e.g. 34-7N-63W"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      {err && (
        <div className="dd-error"><strong>Could not load tracts</strong>{err}</div>
      )}
      {!err && tracts === null && <div className="dd-loading">Loading tracts…</div>}
      {tracts !== null && visible.length === 0 && (
        <div className="dd-loading">No tracts match "{query}".</div>
      )}

      <div className="dd-tract-rows">
        {visible.map(t => (
          <Link key={t.trs} to={`/deal-desk/tracts/${encodeURIComponent(t.trs)}`} className="dd-tract-row">
            <span className="trs">{t.trs}</span>
            <span className="county">{t.county} County</span>
            <span className="meta">{t.chainLength} links</span>
            <span className="meta untraced">{fmtAc(t.untracedTotal)} of {fmtAc(t.grossAcres)} ac untraced</span>
            <span className="meta">{t.dealCount} deal{t.dealCount === 1 ? '' : 's'}</span>
            <span className={`conf ${t.confidence >= 70 ? 'high' : t.confidence >= 45 ? 'mid' : 'low'}`}>
              {t.confidence}%
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function fmtAc(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}
